/**
 * Professional Links Card Component
 * Displays and allows editing of LinkedIn, Twitter, Portfolio URLs
 * Plus custom "Others" links that users can add/remove
 * Matches design style from My Profile page
 */

import React, { useState, useEffect } from 'react';
import { Link2, PenSquare, X, Check, Linkedin, Globe, Plus, Trash2 } from 'lucide-react';
import { CandidateProfile } from '../../services/candidateServiceApi';
import { useDirtyState } from '../../hooks/useDirtyState';

// Twitter/X icon component
const TwitterIcon = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

interface OtherLink {
    name: string;
    url: string;
}

interface ProfessionalLinksData {
    linkedin?: string;
    twitter?: string;
    portfolio?: string;
    others?: OtherLink[];
}

interface ProfessionalLinksCardProps {
    profile: CandidateProfile;
    onSave?: (links: ProfessionalLinksData) => Promise<void>;
}

const ProfessionalLinksCard: React.FC<ProfessionalLinksCardProps> = ({ profile, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editedLinks, setEditedLinks] = useState<ProfessionalLinksData>({
        linkedin: '',
        twitter: '',
        portfolio: '',
        others: []
    });

    // Get links from profile - check new professionalLinks structure first, then fallbacks
    const professionalLinks = (profile as any)?.professionalLinks;
    const linkedin = professionalLinks?.linkedin ||
                    profile?.linkedInUrl ||
                    profile?.linkedin_url ||
                    '';
    const twitter = professionalLinks?.twitter || '';
    const portfolio = professionalLinks?.portfolio ||
                     profile?.portfolio_url ||
                     '';
    const others: OtherLink[] = professionalLinks?.others || [];

    // Track dirty state for save button
    const { isDirty, setOriginal, reset: resetDirty } = useDirtyState(editedLinks);

    // Initialize edited links when entering edit mode
    useEffect(() => {
        if (isEditing) {
            const initialLinks = {
                linkedin,
                twitter,
                portfolio,
                others: [...others]
            };
            setEditedLinks(initialLinks);
            setOriginal(initialLinks);
        } else {
            resetDirty();
        }
    }, [isEditing]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedLinks({ linkedin: '', twitter: '', portfolio: '', others: [] });
    };

    const handleSave = async () => {
        if (onSave) {
            setIsSaving(true);
            try {
                // Filter out empty "others" entries
                const cleanedLinks = {
                    ...editedLinks,
                    others: editedLinks.others?.filter(o => o.name.trim() && o.url.trim()) || []
                };
                await onSave(cleanedLinks);
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to save professional links:', error);
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const handleLinkChange = (field: 'linkedin' | 'twitter' | 'portfolio', value: string) => {
        setEditedLinks(prev => ({ ...prev, [field]: value }));
    };

    const handleAddOther = () => {
        setEditedLinks(prev => ({
            ...prev,
            others: [...(prev.others || []), { name: '', url: '' }]
        }));
    };

    const handleRemoveOther = (index: number) => {
        setEditedLinks(prev => ({
            ...prev,
            others: prev.others?.filter((_, i) => i !== index) || []
        }));
    };

    const handleOtherChange = (index: number, field: 'name' | 'url', value: string) => {
        setEditedLinks(prev => ({
            ...prev,
            others: prev.others?.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            ) || []
        }));
    };

    const hasAnyLinks = linkedin || twitter || portfolio || others.length > 0;

    // Link item component for display mode
    const LinkItem = ({ icon: Icon, label, url, color }: {
        icon: React.ElementType;
        label: string;
        url: string;
        color: string;
    }) => {
        if (!url) return null;
        return (
            <a
                href={url.startsWith('http') ? url : `https://${url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 border border-[#F4F4F4] rounded-lg hover:border-[#015FA6]/30 hover:bg-[#F8FAFC] transition-colors group"
            >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
                    <Icon size={16} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#515151]">{label}</p>
                    <p className="text-xs text-[#757575] truncate">{url}</p>
                </div>
                <svg
                    className="w-4 h-4 text-[#A5A5A5] group-hover:text-[#015FA6] transition-colors flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
            </a>
        );
    };

    return (
        <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 w-full font-manrope">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <Link2 size={20} className="text-[#0B111E]" />
                    <h3 className="text-lg font-semibold text-[#0B111E]">Professional Links</h3>
                </div>
                {!isEditing ? (
                    <button
                        onClick={handleEdit}
                        className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                    >
                        <PenSquare size={20} />
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCancel}
                            className="text-gray-500 hover:bg-gray-100 p-2 rounded-full transition-colors"
                            disabled={isSaving}
                        >
                            <X size={20} />
                        </button>
                        <button
                            onClick={handleSave}
                            className={`p-2 rounded-full transition-colors ${
                                isDirty ? 'text-green-600 hover:bg-green-50' : 'text-gray-300 cursor-not-allowed'
                            }`}
                            disabled={isSaving || !isDirty}
                        >
                            <Check size={20} />
                        </button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <div className="space-y-4">
                    {/* LinkedIn */}
                    <div>
                        <label className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                            <Linkedin size={14} />
                            LinkedIn
                        </label>
                        <input
                            type="url"
                            value={editedLinks.linkedin || ''}
                            onChange={(e) => handleLinkChange('linkedin', e.target.value)}
                            placeholder="https://linkedin.com/in/yourprofile"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                        />
                    </div>

                    {/* Twitter */}
                    <div>
                        <label className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                            <TwitterIcon size={14} />
                            Twitter / X
                        </label>
                        <input
                            type="url"
                            value={editedLinks.twitter || ''}
                            onChange={(e) => handleLinkChange('twitter', e.target.value)}
                            placeholder="https://twitter.com/yourusername"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                        />
                    </div>

                    {/* Portfolio */}
                    <div>
                        <label className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                            <Globe size={14} />
                            Portfolio
                        </label>
                        <input
                            type="url"
                            value={editedLinks.portfolio || ''}
                            onChange={(e) => handleLinkChange('portfolio', e.target.value)}
                            placeholder="https://yourportfolio.com"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                        />
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 pt-4">
                        <p className="text-xs text-gray-500 mb-3">Other Links</p>

                        {/* Others list */}
                        {editedLinks.others?.map((other, index) => (
                            <div key={index} className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={other.name}
                                    onChange={(e) => handleOtherChange(index, 'name', e.target.value)}
                                    placeholder="Name (e.g., Dribbble)"
                                    className="w-1/3 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                />
                                <input
                                    type="url"
                                    value={other.url}
                                    onChange={(e) => handleOtherChange(index, 'url', e.target.value)}
                                    placeholder="https://..."
                                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#015FA6]"
                                />
                                <button
                                    onClick={() => handleRemoveOther(index)}
                                    className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}

                        {/* Add other link button */}
                        <button
                            onClick={handleAddOther}
                            className="flex items-center gap-2 text-sm text-[#015FA6] hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors"
                        >
                            <Plus size={16} />
                            Add another link
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    {hasAnyLinks ? (
                        <>
                            <LinkItem
                                icon={Linkedin}
                                label="LinkedIn"
                                url={linkedin}
                                color="bg-[#0A66C2]"
                            />
                            <LinkItem
                                icon={TwitterIcon}
                                label="Twitter / X"
                                url={twitter}
                                color="bg-black"
                            />
                            <LinkItem
                                icon={Globe}
                                label="Portfolio"
                                url={portfolio}
                                color="bg-[#015FA6]"
                            />
                            {/* Render other links */}
                            {others.map((other, index) => (
                                <LinkItem
                                    key={index}
                                    icon={Link2}
                                    label={other.name}
                                    url={other.url}
                                    color="bg-[#6B7280]"
                                />
                            ))}
                        </>
                    ) : (
                        <div className="text-center py-4">
                            <div className="w-12 h-12 rounded-full bg-[#F8F9FA] flex items-center justify-center mx-auto mb-2">
                                <Link2 size={20} className="text-[#A5A5A5]" />
                            </div>
                            <p className="text-sm text-[#757575]">No professional links added yet</p>
                            <p className="text-xs text-[#A5A5A5]">Click edit to add your social and portfolio links</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProfessionalLinksCard;
